/**
 * ResourceEditDialog - Modal for editing existing resources
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  PROGRAMS, 
  LIBRARIES, 
  RESOURCE_TYPES, 
  INITIAL_FORM 
} from "./ResourceConstants";

export function ResourceEditDialog({ 
  token, 
  resource, 
  isOpen, 
  onOpenChange, 
  onEditSuccess 
}) {
  const [editForm, setEditForm] = useState(INITIAL_FORM);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);

  // Initialize form when resource changes
  useEffect(() => {
    if (resource) {
      setEditForm({
        file: null,
        title: resource.title || "",
        description: resource.description || "",
        resource_type: resource.resource_type || "pdf",
        vault_chamber: resource.vault_chamber || "",
        program: resource.program || "",
        module_id: resource.module_id || "",
        lesson_id: resource.lesson_id || "",
        tags: (resource.tags || []).join(", "),
      });
    }
  }, [resource]);

  const fetchModules = useCallback(async (program) => {
    if (!program) {
      setModules([]);
      setLessons([]);
      return;
    }
    
    const courseMap = {
      wake_up: "course_wake_up",
      burn_the_map: "course_burn_map",
      whole: "course_whole",
    };
    const courseId = courseMap[program];
    
    if (!courseId) {
      setModules([]);
      setLessons([]);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/admin/modules`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { course_id: courseId }
      });
      setModules(res.data || []);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
      setModules([]);
    }
  }, [token]);

  const fetchLessons = useCallback(async (moduleId) => {
    if (!moduleId) {
      setLessons([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { module_id: moduleId }
      });
      setLessons(res.data || []);
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
      setLessons([]);
    }
  }, [token]);

  useEffect(() => {
    if (editForm.program) {
      fetchModules(editForm.program);
    }
  }, [editForm.program, fetchModules]);

  useEffect(() => {
    if (editForm.module_id) {
      fetchLessons(editForm.module_id);
    }
  }, [editForm.module_id, fetchLessons]);

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!resource) return;

    try {
      const params = new URLSearchParams();
      if (editForm.title) params.append("title", editForm.title.trim());
      if (editForm.description !== undefined) params.append("description", editForm.description);
      if (editForm.vault_chamber) params.append("vault_chamber", editForm.vault_chamber);
      if (editForm.program) params.append("program", editForm.program);
      if (editForm.resource_type) params.append("resource_type", editForm.resource_type);
      params.append("module_id", editForm.module_id || "");
      params.append("lesson_id", editForm.lesson_id || "");
      if (editForm.tags !== undefined) params.append("tags", editForm.tags);

      await axios.put(
        `${API}/admin/resources/${resource.resource_id}?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Resource updated successfully!");
      onOpenChange(false);
      onEditSuccess?.();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(error.response?.data?.detail || "Failed to update resource");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-emerald-900 border-white/10 text-white max-w-lg !p-0 !gap-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10 shrink-0">
          <DialogTitle className="text-white text-xl">Edit Resource</DialogTitle>
          <DialogDescription className="text-white/60">
            Update the resource metadata and categorization
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSaveEdit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Title */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Title *</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1 bg-white/10 border-white/20 text-white h-9"
                placeholder="Resource title"
                required
                data-testid="edit-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Short Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 bg-white/10 border-white/20 text-white min-h-[50px]"
                placeholder="Brief description"
                data-testid="edit-description-input"
              />
            </div>

            {/* Program and Library */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 font-medium text-sm">Program *</Label>
                <Select 
                  value={editForm.program}
                  onValueChange={(v) => setEditForm(prev => ({ 
                    ...prev, 
                    program: v,
                    module_id: "",
                    lesson_id: "",
                  }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9" data-testid="edit-program-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    {PROGRAMS.map(prog => (
                      <SelectItem key={prog.value} value={prog.value} className="text-white hover:bg-white/10">
                        <span className="flex items-center gap-2">
                          <prog.icon className="w-3 h-3" style={{ color: prog.color }} />
                          {prog.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/80 font-medium text-sm">Library *</Label>
                <Select 
                  value={editForm.vault_chamber}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, vault_chamber: v }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9" data-testid="edit-library-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    {LIBRARIES.map(lib => (
                      <SelectItem key={lib.value} value={lib.value} className="text-white hover:bg-white/10">
                        {lib.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Resource Type */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Resource Type</Label>
              <Select 
                value={editForm.resource_type}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, resource_type: v }))}
              >
                <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-emerald-900 border-white/20">
                  {RESOURCE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Tags (comma-separated)</Label>
              <Input
                value={editForm.tags}
                onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                className="mt-1 bg-white/10 border-white/20 text-white h-9"
                placeholder="e.g. worksheet, reflection, week-1"
                data-testid="edit-tags-input"
              />
            </div>

            {/* Module/Lesson linking */}
            {modules.length > 0 && (
              <div>
                <Label className="text-white/80 font-medium text-sm">Link to Module (Optional)</Label>
                <Select 
                  value={editForm.module_id || "none"}
                  onValueChange={(v) => setEditForm(prev => ({ 
                    ...prev, 
                    module_id: v === "none" ? "" : v,
                    lesson_id: ""
                  }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    <SelectItem value="none" className="text-white/60 hover:bg-white/10">None</SelectItem>
                    {modules.map(module => (
                      <SelectItem key={module.module_id} value={module.module_id} className="text-white hover:bg-white/10">
                        {module.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {lessons.length > 0 && editForm.module_id && (
              <div>
                <Label className="text-white/80 font-medium text-sm">Link to Lesson (Optional)</Label>
                <Select 
                  value={editForm.lesson_id || "none"}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, lesson_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                    <SelectValue placeholder="Select lesson" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    <SelectItem value="none" className="text-white/60 hover:bg-white/10">None</SelectItem>
                    {lessons.map(lesson => (
                      <SelectItem key={lesson.lesson_id} value={lesson.lesson_id} className="text-white hover:bg-white/10">
                        {lesson.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-white/10 bg-emerald-900 shrink-0">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 h-9"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary h-9"
                data-testid="save-edit-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
